import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Stethoscope } from 'lucide-react';

const MeasuresSection = ({ data = {}, onChange }) => {
  const handleCheckboxChange = (measureId) => {
    const measures = data.measures || [];
    const newMeasures = measures.includes(measureId)
      ? measures.filter(id => id !== measureId)
      : [...measures, measureId];
    
    onChange({ ...data, measures: newMeasures });
  };

  const handleInputChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const measuresOptions = [
    { id: 'oxygen_mask', label: 'Sauerstoffgabe per Maske' },
    { id: 'oxygen_nasal', label: 'Sauerstoffgabe per Nasensonde' },
    { id: 'wound_care', label: 'Wundversorgung' },
    { id: 'fracture_splint', label: 'Schienung von Frakturen' },
    { id: 'vacuum_mattress', label: 'Vakuummatratze' },
    { id: 'cervical_collar', label: 'HWS-Schiene (Stifneck)' },
    { id: 'iv_access', label: 'Venöser Zugang gelegt' },
    { id: 'medication_given', label: 'Medikamentengabe' }
  ];

  return (
    <Card className="section-card border-primary/30">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Stethoscope className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl">Durchgeführte Maßnahmen</CardTitle>
            <CardDescription className="text-base mt-1">
              Dokumentation der präklinischen Versorgung
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Measures Checkboxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {measuresOptions.map((option) => {
            const isChecked = (data.measures || []).includes(option.id);
            
            return (
              <div
                key={option.id}
                data-testid={`measure-${option.id.replaceAll('_', '-')}-option`}
                role="checkbox"
                aria-checked={isChecked}
                aria-label={option.label}
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    handleCheckboxChange(option.id);
                  }
                }}
                onClick={() => handleCheckboxChange(option.id)}
                className={`
                  flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${isChecked 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted hover:border-primary/50 hover:bg-muted/50'
                  }
                `}
              >
                <div className={`
                  w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                  ${isChecked ? 'bg-primary border-primary' : 'border-muted-foreground'}
                `}>
                  {isChecked && (
                    <svg className="w-3 h-3 text-primary-foreground" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <Label className="cursor-pointer text-sm font-medium flex-1">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>

        {/* Oxygen Flow Rate */}
        {((data.measures || []).includes('oxygen_mask') || (data.measures || []).includes('oxygen_nasal')) && (
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg border border-muted">
            <Label htmlFor="oxygen_flow" className="text-sm font-semibold">
              Sauerstoff-Flussrate (Liter/min)
            </Label>
            <div className="flex items-center gap-4">
              <Input
                id="oxygen_flow"
                data-testid="oxygen-flow-input"
                type="number"
                min="1"
                max="15"
                value={data.oxygen_flow || ''}
                onChange={(e) => handleInputChange('oxygen_flow', e.target.value)}
                placeholder="z.B. 4"
                className="max-w-[120px]"
              />
              <input
                data-testid="oxygen-flow-slider"
                aria-label="Sauerstoff-Flussrate"
                type="range"
                min="1"
                max="15"
                value={data.oxygen_flow || 4}
                onChange={(e) => handleInputChange('oxygen_flow', e.target.value)}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm font-medium min-w-[60px]">
                {data.oxygen_flow || 4} L/min
              </span>
            </div>
          </div>
        )}

        {/* Medication Details */}
        {(data.measures || []).includes('medication_given') && (
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg border border-muted">
            <Label htmlFor="medication_details" className="text-sm font-semibold">
              Medikamentendetails
            </Label>
            <Input
              id="medication_details"
              data-testid="medication-details-input"
              type="text"
              value={data.medication_details || ''}
              onChange={(e) => handleInputChange('medication_details', e.target.value)}
              placeholder="z.B. ASS 500mg p.o., NaCl 0,9% 500ml i.v."
              className="w-full"
            />
          </div>
        )}

        {/* Wound Care Details */}
        {(data.measures || []).includes('wound_care') && (
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg border border-muted">
            <Label htmlFor="wound_details" className="text-sm font-semibold">
              Details zur Wundversorgung
            </Label>
            <Input
              id="wound_details"
              data-testid="wound-details-input"
              type="text"
              value={data.wound_details || ''}
              onChange={(e) => handleInputChange('wound_details', e.target.value)}
              placeholder="z.B. Steriler Verband an linkem Unterarm"
              className="w-full"
            />
          </div>
        )}

        {/* Splint Details */}
        {(data.measures || []).includes('fracture_splint') && (
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg border border-muted">
            <Label htmlFor="splint_details" className="text-sm font-semibold">
              Details zur Schienung
            </Label>
            <Input
              id="splint_details"
              data-testid="splint-details-input"
              type="text"
              value={data.splint_details || ''}
              onChange={(e) => handleInputChange('splint_details', e.target.value)}
              placeholder="z.B. Schienung rechter Unterschenkel"
              className="w-full"
            />
          </div>
        )}

        {/* Additional Measures Free Text */}
        <div className="space-y-2">
          <Label htmlFor="additional_measures" className="text-sm font-semibold">
            Weitere Maßnahmen (Freitext)
          </Label>
          <Input
            id="additional_measures"
            data-testid="additional-measures-input"
            type="text"
            value={data.additional_measures || ''}
            onChange={(e) => handleInputChange('additional_measures', e.target.value)}
            placeholder="Weitere durchgeführte Maßnahmen..."
            className="w-full"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default MeasuresSection;
